import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { PenLine, ExternalLink, Link2, LogOut, QrCode, ChevronRight, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { StatusBadge, Empty } from "@/components/admin/common";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, publicCardUrl, copyText } from "@/lib/api";

const PortalCard = ({ c }) => {
  const nav = useNavigate();
  return (
    <div className="panel flex flex-col gap-4 p-5 sm:flex-row sm:items-center" data-testid={`portal-card-${c.slug}`}>
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full border bg-zinc-100">{c.avatar_url && <img src={fileUrl(c.avatar_url)} alt="" className="h-full w-full object-cover" />}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2"><span className="font-medium">{c.display_name || c.name}</span><StatusBadge status={c.status} /></div>
        <div className="mt-0.5 truncate font-mono-ui text-xs text-zinc-500">{publicCardUrl(c.slug)}</div>
        <div className="mt-1 text-xs text-zinc-500">{c.views} visite totali</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => nav(`/portale/card/${c.id}`)} data-testid={`portal-edit-${c.slug}`}><PenLine className="mr-1.5 h-4 w-4" />Modifica</Button>
        {c.status === "published" && <Button variant="outline" onClick={() => window.open(publicCardUrl(c.slug), "_blank")} data-testid={`portal-open-${c.slug}`}><ExternalLink className="h-4 w-4" /></Button>}
        <Button variant="outline" data-testid={`portal-copy-${c.slug}`} onClick={async () => { await copyText(publicCardUrl(c.slug)); toast.success("Link copiato"); }}><Link2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

const PortalQrRow = ({ q }) => (
  <Link to={`/portale/qr/${q.id}`} className="panel flex items-center gap-4 p-5 transition-colors hover:bg-zinc-50" data-testid={`portal-qr-${q.code}`}>
    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-zinc-100"><QrCode className="h-5 w-5" strokeWidth={1.5} /></div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2"><span className="font-medium">{q.name}</span>{!q.active && <span className="text-[11px] text-zinc-400">disattivato</span>}</div>
      <div className="truncate text-xs text-zinc-500">{q.target_url}</div>
    </div>
    <div className="text-right text-xs text-zinc-500" data-testid={`portal-qr-scans-${q.code}`}>{q.mode === "dynamic" ? <><div className="text-base tabular-nums text-zinc-900">{q.scans}</div>scansioni</> : "Diretto"}</div>
    <ChevronRight className="h-4 w-4 text-zinc-400" />
  </Link>
);

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const [cards, setCards] = useState(null);
  const [qrs, setQrs] = useState([]);
  useEffect(() => {
    api.get("/cards").then((r) => setCards(r.data));
    api.get("/qr-links").then((r) => setQrs(r.data));
  }, []);
  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <header className="flex items-center justify-between border-b border-black/[0.06] bg-white/80 px-5 py-4 backdrop-blur-xl sm:px-10">
        <Logo size={28} />
        <div className="flex items-center gap-4">
          <span className="hidden text-xs text-zinc-500 sm:block" data-testid="portal-user-email">{user?.email}</span>
          <Link to="/portale/password" className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-black" data-testid="portal-change-password"><KeyRound className="h-4 w-4" />Password</Link>
          <button onClick={logout} className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-black" data-testid="portal-logout"><LogOut className="h-4 w-4" />Esci</button>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-12 sm:px-8">
        <div className="eyebrow mb-2">La tua area</div>
        <h1 className="font-display text-4xl sm:text-5xl" data-testid="portal-title">Le tue card</h1>
        <p className="mt-3 max-w-lg text-sm text-zinc-500">Aggiorna contatti, link, testi, foto e documenti. Le modifiche salvate sono subito visibili online e nel file contatto, senza cambiare il QR.</p>
        <div className="mt-10 space-y-3">
          {cards && !cards.length && <Empty title="Nessuna card">Non ci sono ancora card associate al tuo account.</Empty>}
          {(cards || []).map((c) => <PortalCard key={c.id} c={c} />)}
        </div>
        {qrs.length > 0 && (
          <section className="mt-14" data-testid="portal-qr-section">
            <h2 className="font-display text-2xl">I tuoi QR</h2>
            <p className="mt-2 text-sm text-zinc-500">Controlla le scansioni, cambia il link di destinazione e scarica il QR.</p>
            <div className="mt-6 space-y-3">{qrs.map((q) => <PortalQrRow key={q.id} q={q} />)}</div>
          </section>
        )}
      </main>
    </div>
  );
}
